import React from 'react';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import trim from 'lodash/trim';

import { TFunction, Trans } from '@lyearn/i18n';
import {
  ErrorPosition,
  Fields,
  FieldType,
  useFormFieldValue,
  VisibilityOperators,
} from '@/components/Form';
import { DEFAULT_ASSIGNMENT_RESTRICTION } from '@/components/Form/components/AssignmentRestriction/consts';
import FormEditor from '@/components/Form/components/Editor';
import { renderFormAssignmentRestriction } from '@/components/Form/renderers/renderAssignmentRestriction';
import { renderTagsForContent } from '@/components/Form/renderers/renderTags';
import { renderWorkspaceSettings } from '@/components/Form/renderers/renderWorkspaceSettings';
import { validateMedias } from '@/components/Form/validator';
import { AppConfig } from '@/hooks/useAppConfig';
import { ExerciseQuestionsFragment } from '@/modules/content/modules/exercise/fragments/generated/ExerciseQuestions';
import Duration from '@/modules/corporate/components/Duration';
import AddCertificate from '@/modules/corporate/modules/certificates/components/AddCertificate';
import { getDefaultCertificateSettings } from '@/modules/corporate/modules/certificates/consts';
import isInvalidCertificateSettings from '@/modules/corporate/modules/certificates/utils/validateCertificateSettings';
import {
  adaptTagsOnEdit,
  areRequiredTagsEmpty,
  DEFAULT_TAG_VALUE,
} from '@/modules/corporate/modules/tags/utils';
import { DEFAULT_WORKSPACE_IDS } from '@/modules/corporate/modules/workspaceSettings/const';
import { isWorkspaceSettingsEmpty } from '@/modules/corporate/modules/workspaceSettings/utils';
import AddSkills from '@/modules/skills/components/AddSkills';
import {
  CreateExerciseInput,
  CreateMediaInput,
  EntityTagType,
  ResourceReviewTypeEnum,
  ResourceTypeEnum,
  ReviewerConfigInput,
  ReviewerTypeEnum,
} from '@/types/schema';

import { ExerciseEditorDetailFragment } from '../../../../graphql/fragments/generated/ExerciseEditorDetail';
import { getReviewOptions } from '../../consts';
import { FormSection } from '../../interfaces';
import AddScorecards from '../AddScorecards';
import Questions from '../Questions';
import { DEFAULT_QUESTION_ITEM } from '../Questions/consts';
import { isQuestionsEmpty } from '../Questions/utils';
import SubmissionReview from '../SubmissionReview';
import { DEFAULT_REVIEWER_CONFIG } from '../SubmissionReview/consts';
import { validateReviewerConfigError } from '../SubmissionReview/utils';
import { isAnyExerciseQuestionInvalid } from './validations';

export const getInitialValuesBasedOnSettings = (
  site: AppConfig['site'],
  defaultCover: CreateMediaInput,
) => ({
  name: '',
  objective: '',
  instruction: {},
  duration: 30 * 60,
  cover: defaultCover,
  scorecardIds: [],
  tags: DEFAULT_TAG_VALUE,
  reviewerInstruction: {},
  exerciseQuestions: [DEFAULT_QUESTION_ITEM],
  advanceSettings: {
    allowComments: true,
  },
  certificateSettings: getDefaultCertificateSettings({
    disableLinkedInSharing: site.certificateSharingSettings.disableLinkedInSharing,
    disableSocialSharing: site.certificateSharingSettings.disableSocialSharing,
  }),
  reviewType: ResourceReviewTypeEnum.MandatoryReview,
  reviewerConfig: DEFAULT_REVIEWER_CONFIG,
  skillIds: [],
  subWorkspaceIds: DEFAULT_WORKSPACE_IDS,
  assignmentRestriction: site.enableAssignmentRestriction
    ? DEFAULT_ASSIGNMENT_RESTRICTION
    : undefined,
});

const getDuplicateExerciseQuestions = (
  exerciseQuestions?: ExerciseQuestionsFragment[] | undefined,
) => exerciseQuestions?.map((question) => omit(question, 'id'));

export const getInitialValues = ({
  exercise,
  site,
  defaultCover,
  isDuplicating,
  t,
}: {
  exercise: ExerciseEditorDetailFragment;
  site: AppConfig['site'];
  defaultCover: CreateMediaInput;
  isDuplicating: boolean;
  t: TFunction;
}) => {
  if (!exercise) {
    return getInitialValuesBasedOnSettings(site, defaultCover);
  }

  const {
    name,
    instruction,
    duration,
    scorecardIds,
    reviewerInstruction,
    exerciseQuestions,
    advanceSettings,
    certificateSettings,
    tags,
    objective,
    reviewType,
    reviewerConfig,
    skillIds,
    subWorkspaces,
    sharedByWorkspace,
    cover,
    assignmentRestriction,
  } = exercise;

  const adaptedReviewerConfig =
    !reviewerConfig || (isDuplicating && sharedByWorkspace)
      ? DEFAULT_REVIEWER_CONFIG
      : reviewerConfig;

  const { enableAssignmentRestriction } = site;

  return {
    name: isDuplicating ? `${t('Common:CopyOf')} ${name}` : name,
    objective: objective || '',
    instruction,
    duration,
    scorecardIds: isDuplicating && sharedByWorkspace ? [] : scorecardIds,
    reviewerInstruction,
    reviewType,
    reviewerConfig: adaptedReviewerConfig,
    skillIds: skillIds ?? [],
    exerciseQuestions: isDuplicating
      ? getDuplicateExerciseQuestions(exerciseQuestions)
      : exerciseQuestions,
    advanceSettings,
    tags: adaptTagsOnEdit(tags as EntityTagType[] | null | undefined),
    certificateSettings:
      certificateSettings ||
      getDefaultCertificateSettings({
        disableLinkedInSharing: site.certificateSharingSettings.disableLinkedInSharing,
        disableSocialSharing: site.certificateSharingSettings.disableSocialSharing,
      }),
    subWorkspaceIds: subWorkspaces || DEFAULT_WORKSPACE_IDS,
    cover: isDuplicating ? omit(cover, 'mediaId') : undefined,
    assignmentRestriction: enableAssignmentRestriction
      ? assignmentRestriction || DEFAULT_ASSIGNMENT_RESTRICTION
      : undefined,
  };
};

export const getFormFields = (
  t: TFunction,
  payload: { isEditMode: boolean; formInitialValues: any },
): Fields => {
  return {
    name: {
      name: 'name',
      placeholder: t('Exercise:Creator.TitlePlaceholder'),
      label: t('Common:Form.Title'),
      type: FieldType.Text,
      size: 'large',
      props: {
        characterLimit: 100,
      },
    },
    objective: {
      name: 'objective',
      placeholder: t('Exercise:Creator.ObjectivePlaceholder'),
      label: t('Common:Form.Objective'),
      type: FieldType.Multiline,
      size: 'large',
      props: {
        characterLimit: 160,
        rows: 3,
        maxRows: 3,
      },
    },
    instruction: {
      name: 'instruction',
      label: t('Common:Form.Instructions'),
      required: false,
      type: FieldType.Custom,
      Component: ({ value, setFieldValue }) => {
        const fieldKey = 'instruction';

        return (
          <FormEditor
            medias={value?.medias}
            mediasFieldKey={fieldKey}
            placeholder={t('Exercise:Creator.DescribeTheExercise')}
            setFieldValue={setFieldValue}
            text={value?.text}
            textFieldKey={fieldKey}
          />
        );
      },
    },
    duration: {
      name: 'duration',
      type: FieldType.Custom,
      label: t('Common:Duration'),
      Component: ({ value, onChange, touched, error }) => {
        const showError = Boolean(touched && error);

        return <Duration error={showError} value={value} onChange={onChange} />;
      },
    },
    subWorkspaceIds: {
      name: 'subWorkspaceIds',
      type: FieldType.Custom,
      Component: renderWorkspaceSettings(t('Common:ResourceType.Exercise')),
    },
    exerciseQuestions: {
      name: 'exerciseQuestions',
      type: FieldType.Custom,
      errorPosition: ErrorPosition.Custom,
      Component: ({ value, error, touched, onChange, setErrorElement }) => {
        const showError = touched && error;

        return (
          <Questions
            error={showError ? error : undefined}
            questions={value}
            setErrorElement={setErrorElement}
            onChange={onChange}
          />
        );
      },
    },
    reviewType: {
      name: 'reviewType',
      type: FieldType.Picklist,
      disabled:
        (payload.formInitialValues.reviewerConfig as ReviewerConfigInput).type ===
        ReviewerTypeEnum.Peer,
      picklistValues: getReviewOptions(t),
      placeholder: t('Common:Actions.Select'),
      size: 'large',
      props: {
        className: 'w-1/2 pe-6',
      },
    },
    reviewerConfig: {
      name: 'reviewerConfig',
      errorPosition: ErrorPosition.Custom,
      type: FieldType.SlowCustom,
      Component: ({ value, onChange, touched, error }) => {
        const { formInitialValues, isEditMode } = payload;
        const showError = touched && error;
        const reviewType = useFormFieldValue('reviewType');

        return (
          <SubmissionReview
            initialReviewerConfig={formInitialValues.reviewerConfig}
            isEditMode={isEditMode}
            reviewType={reviewType}
            reviewerConfig={value}
            showError={showError}
            onReviewerConfigChange={onChange}
          />
        );
      },
      visibility: [
        {
          fieldKey: 'reviewType',
          value: ResourceReviewTypeEnum.NoReview,
          operator: VisibilityOperators.NotEqual,
        },
      ],
    },
    scorecardIds: {
      name: 'scorecardIds',
      type: FieldType.Custom,
      Component: ({ value, onChange }) => (
        <AddScorecards selectedScorecardIds={value} onChange={onChange} />
      ),
      visibility: [
        {
          fieldKey: 'reviewType',
          value: ResourceReviewTypeEnum.NoReview,
          operator: VisibilityOperators.NotEqual,
        },
      ],
    },
    skillIds: {
      name: 'skillIds',
      type: FieldType.Custom,
      Component: ({ value, onChange }) => {
        return <AddSkills skillIds={value} onChange={onChange} />;
      },
    },
    tags: {
      name: 'tags',
      type: FieldType.Custom,
      Component: renderTagsForContent,
    },
    reviewerInstruction: {
      name: 'reviewerInstruction',
      label: t('Exercise:Creator.InstructionsForReviewers'),
      required: false,
      type: FieldType.Custom,
      Component: ({ value, setFieldValue }) => {
        const fieldKey = 'reviewerInstruction';

        return (
          <FormEditor
            medias={value?.medias}
            mediasFieldKey={fieldKey}
            placeholder={t('Exercise:Creator.AddInstructionsForReviewers')}
            setFieldValue={setFieldValue}
            text={value?.text}
            textFieldKey={fieldKey}
          />
        );
      },
      visibility: [
        {
          fieldKey: 'reviewType',
          value: ResourceReviewTypeEnum.NoReview,
          operator: VisibilityOperators.NotEqual,
        },
      ],
    },
    certificateSettings: {
      name: 'certificateSettings',
      type: FieldType.SlowCustom,
      Component: ({ onChange, errors, formik }) => {
        const showError = Boolean(formik.touched?.certificateSettings && errors?.certificate);
        const certificateSettings = useFormFieldValue('certificateSettings');
        const reviewType = useFormFieldValue('reviewType');

        return (
          <AddCertificate
            certificateSettings={certificateSettings}
            contentType={ResourceTypeEnum.Exercise}
            error={showError}
            showConditionSettings={reviewType !== ResourceReviewTypeEnum.NoReview}
            onChange={onChange}
          />
        );
      },
    },
    assignmentRestriction: {
      name: 'assignmentRestriction',
      type: FieldType.Custom,
      Component: renderFormAssignmentRestriction({
        contentName: t('Common:ResourceType.Exercise'),
      }),
    },
  };
};

export const getSectionLayout = ({
  t,
  subWorkspacesEnabled,
  enableAssignmentRestriction,
}: {
  t: TFunction;
  subWorkspacesEnabled: boolean;
  enableAssignmentRestriction: boolean;
}) => [
  {
    id: FormSection.Basic,
    layout: ['name', 'objective', 'instruction', 'duration'],
  },
  ...(enableAssignmentRestriction
    ? [
        {
          id: FormSection.AssignmentRestriction,
          layout: ['assignmentRestriction'],
        },
      ]
    : []),
  {
    id: FormSection.Questions,
    name: t('Exercise:Creator.Questions'),
    description: t('Exercise:Creator.QuestionsDescription'),
    layout: ['exerciseQuestions'],
  },
  {
    id: FormSection.NativeSettings,
    subSections: [
      ...(subWorkspacesEnabled
        ? [
            {
              id: FormSection.WorkspaceSettings,
              layout: ['subWorkspaceIds'],
            },
          ]
        : []),
      {
        id: FormSection.Reviewer,
        name: t('Exercise:Creator.SubmissionReview'),
        layout: ['reviewType', 'reviewerConfig', 'reviewerInstruction', 'scorecardIds'],
        classes: {
          layout: {
            className: 'space-y-40',
          },
        },
      },
    ],
  },
  {
    id: FormSection.Manage,
    subSections: [
      {
        id: FormSection.Skills,
        name: t('Common:RoleDetails.Skills'),
        description: t('Skills:AddSkills.AddSkillsToBeAssessed'),
        layout: ['skillIds'],
      },
      {
        id: FormSection.Certificate,
        layout: ['certificateSettings'],
      },
      {
        id: FormSection.Tags,
        name: t('Common:Form.Tags'),
        description: t('Common:Form.TagsDescription'),
        layout: ['tags'],
      },
    ],
  },
];

export const isTitleEmpty = (values: CreateExerciseInput) => {
  return !values.name || isEmpty(trim(values.name));
};

export const isObjectiveEmpty = (values: CreateExerciseInput) => {
  return !values.objective || isEmpty(trim(values.objective));
};

export const isScorecardEmpty = (values: CreateExerciseInput) => {
  return values.reviewType !== ResourceReviewTypeEnum.NoReview && !values.scorecardIds?.length;
};

export const validateForm =
  (t: TFunction, subWorkspacesEnabled: boolean) => (values: CreateExerciseInput) => {
    const errors = validateFormForPreview(t, values);

    if (!values.duration) {
      errors.duration = <Trans i18nKey="Common:Error.DurationEmpty" />;
    }

    const reviewerInstructionMediaError = validateMedias(values.reviewerInstruction?.medias);
    if (reviewerInstructionMediaError) {
      errors.reviewerInstruction = reviewerInstructionMediaError;
    }

    const mediaError = validateMedias(values.instruction?.medias);
    if (mediaError) {
      errors.instruction = mediaError;
    }

    //@ts-ignore
    if (areRequiredTagsEmpty(values.tags)) {
      errors.tags = <Trans i18nKey="Tags:Form.Error.TagsValuesEmpty" />;
    }

    if (isWorkspaceSettingsEmpty(subWorkspacesEnabled, values.subWorkspaceIds)) {
      errors.subWorkspaceIds = <Trans i18nKey="Workspace:WorkspaceSettingsEmpty" />;
    }

    if (isInvalidCertificateSettings(values.certificateSettings)) {
      errors.certificate = true;
    }

    const reviewerConfigError = validateReviewerConfigError(
      values.reviewerConfig,
      values.reviewType,
    );
    if (reviewerConfigError) {
      errors.reviewerConfig = reviewerConfigError;
    }

    return errors;
  };

// validate title, questions scorecard, duration,
export const validateFormForPreview = (t: TFunction, values: CreateExerciseInput) => {
  const errors: any = {};

  if (isTitleEmpty(values)) {
    errors.name = <Trans i18nKey="Exercise:Creator.TitleError" />;
  }

  if (isObjectiveEmpty(values)) {
    errors.objective = <Trans i18nKey="Exercise:Creator.ObjectiveError" />;
  }

  if (isQuestionsEmpty(values.exerciseQuestions!)) {
    errors.exerciseQuestions = <Trans i18nKey="Exercise:Creator.NoQuestionError" />;
  } else if (isAnyExerciseQuestionInvalid(t, values.exerciseQuestions!)) {
    errors.exerciseQuestions = 'questions error';
  }

  if (isScorecardEmpty(values)) {
    errors.scorecardIds = <Trans i18nKey="Exercise:Creator.ScorecardError" />;
  }

  return errors;
};
